<template>
  <div class="form-generator">
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      :label-width="labelWidth"
      :label-position="labelPosition"
      :size="size"
      :disabled="disabled"
      :validate-on-rule-change="validateOnRuleChange"
      @submit.prevent="handleSubmit"
    >
      <!-- 表单字段 -->
      <template v-for="(field, index) in fields" :key="index">
        <!-- 分组标题 -->
        <div v-if="field.type === 'divider'" class="form-divider">
          <div class="divider-title" v-if="field.label">{{ field.label }}</div>
          <el-divider v-if="!field.hideDivider" />
        </div>

        <!-- 表单项 -->
        <el-form-item
          v-else-if="isFieldVisible(field)"
          :label="field.label"
          :prop="field.prop"
          :rules="field.rules"
          :required="field.required"
          :error="field.error"
          :label-width="field.labelWidth"
          :class="field.class"
        >
          <!-- 输入框 -->
          <el-input
            v-if="field.type === 'input' || !field.type"
            v-model="formData[field.prop]"
            :type="field.inputType || 'text'"
            :placeholder="field.placeholder"
            :maxlength="field.maxlength"
            :minlength="field.minlength"
            :show-word-limit="field.showWordLimit"
            :clearable="field.clearable !== false"
            :disabled="field.disabled || disabled"
            :readonly="field.readonly"
            :prefix-icon="field.prefixIcon"
            :suffix-icon="field.suffixIcon"
            :rows="field.rows"
            :autosize="field.autosize"
            @input="(val) => handleFieldChange(field.prop, val)"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          />

          <!-- 选择器 -->
          <el-select
            v-else-if="field.type === 'select'"
            v-model="formData[field.prop]"
            :placeholder="field.placeholder"
            :multiple="field.multiple"
            :clearable="field.clearable !== false"
            :disabled="field.disabled || disabled"
            :filterable="field.filterable !== false"
            :remote="field.remote"
            :remote-method="field.remoteMethod"
            :loading="field.loading"
            :reserve-keyword="field.reserveKeyword !== false"
            :allow-create="field.allowCreate"
            :default-first-option="field.defaultFirstOption"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          >
            <el-option
              v-for="option in field.options"
              :key="option.value"
              :label="option.label"
              :value="option.value"
              :disabled="option.disabled"
            />
          </el-select>

          <!-- 日期选择器 -->
          <el-date-picker
            v-else-if="field.type === 'date'"
            v-model="formData[field.prop]"
            :type="field.dateType || 'date'"
            :placeholder="field.placeholder"
            :format="field.format"
            :value-format="field.valueFormat"
            :disabled="field.disabled || disabled"
            :clearable="field.clearable !== false"
            :shortcuts="field.shortcuts"
            :disabledDate="field.disabledDate"
            :range-separator="field.rangeSeparator"
            :start-placeholder="field.startPlaceholder"
            :end-placeholder="field.endPlaceholder"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          />

          <!-- 开关 -->
          <el-switch
            v-else-if="field.type === 'switch'"
            v-model="formData[field.prop]"
            :disabled="field.disabled || disabled"
            :active-text="field.activeText"
            :inactive-text="field.inactiveText"
            :active-value="field.activeValue"
            :inactive-value="field.inactiveValue"
            :active-color="field.activeColor"
            :inactive-color="field.inactiveColor"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          />

          <!-- 单选框组 -->
          <el-radio-group
            v-else-if="field.type === 'radio'"
            v-model="formData[field.prop]"
            :disabled="field.disabled || disabled"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          >
            <el-radio
              v-for="option in field.options"
              :key="option.value"
              :label="option.value"
              :disabled="option.disabled"
            >
              {{ option.label }}
            </el-radio>
          </el-radio-group>

          <!-- 复选框组 -->
          <el-checkbox-group
            v-else-if="field.type === 'checkbox'"
            v-model="formData[field.prop]"
            :disabled="field.disabled || disabled"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          >
            <el-checkbox
              v-for="option in field.options"
              :key="option.value"
              :label="option.value"
              :disabled="option.disabled"
            >
              {{ option.label }}
            </el-checkbox>
          </el-checkbox-group>

          <!-- 滑块 -->
          <el-slider
            v-else-if="field.type === 'slider'"
            v-model="formData[field.prop]"
            :min="field.min"
            :max="field.max"
            :step="field.step"
            :show-input="field.showInput"
            :range="field.range"
            :disabled="field.disabled || disabled"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          />

          <!-- 评分 -->
          <el-rate
            v-else-if="field.type === 'rate'"
            v-model="formData[field.prop]"
            :max="field.max"
            :disabled="field.disabled || disabled"
            :allow-half="field.allowHalf"
            :colors="field.colors"
            :void-color="field.voidColor"
            :disabled-void-color="field.disabledVoidColor"
            :texts="field.texts"
            :show-text="field.showText"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          />

          <!-- 上传 -->
          <el-upload
            v-else-if="field.type === 'upload'"
            :action="field.action"
            :headers="field.headers"
            :data="field.data"
            :name="field.name"
            :with-credentials="field.withCredentials"
            :show-file-list="field.showFileList !== false"
            :drag="field.drag"
            :accept="field.accept"
            :list-type="field.listType || 'text'"
            :auto-upload="field.autoUpload !== false"
            :file-list="formData[field.prop]"
            :limit="field.limit"
            :disabled="field.disabled || disabled"
            :on-preview="field.onPreview"
            :on-remove="(file, fileList) => handleUploadRemove(field.prop, file, fileList)"
            :on-success="(response, file, fileList) => handleUploadSuccess(field.prop, response, file, fileList)"
            :on-error="field.onError"
            :on-exceed="field.onExceed"
            :before-upload="field.beforeUpload"
          >
            <template #default>
              <el-button type="primary">点击上传</el-button>
              <div class="el-upload__tip" v-if="field.tip">{{ field.tip }}</div>
            </template>
          </el-upload>

          <!-- 自定义组件 -->
          <component
            v-else-if="field.type === 'custom'"
            :is="field.component"
            v-model="formData[field.prop]"
            v-bind="field.props || {}"
            @change="(val) => handleFieldChange(field.prop, val, true)"
          />

          <!-- 表单项说明 -->
          <div v-if="field.description" class="form-item-description">
            {{ field.description }}
          </div>
        </el-form-item>
      </template>

      <!-- 表单操作按钮 -->
      <el-form-item v-if="showButtons" class="form-buttons">
        <slot name="buttons">
          <el-button
            v-if="showCancelButton"
            @click="handleCancel"
            :disabled="loading"
          >
            {{ cancelText }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :loading="loading"
          >
            {{ submitText }}
          </el-button>
        </slot>
      </el-form-item>
    </el-form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { cloneDeep } from 'lodash-es';

const props = defineProps({
  // 表单字段配置
  fields: {
    type: Array,
    required: true
  },
  // 表单数据
  modelValue: {
    type: Object,
    default: () => ({})
  },
  // 表单规则
  rules: {
    type: Object,
    default: () => ({})
  },
  // 表单标签宽度
  labelWidth: {
    type: [String, Number],
    default: '100px'
  },
  // 表单标签位置
  labelPosition: {
    type: String,
    default: 'right'
  },
  // 表单尺寸
  size: {
    type: String,
    default: 'default'
  },
  // 是否禁用表单
  disabled: {
    type: Boolean,
    default: false
  },
  // 规则变化时是否触发验证
  validateOnRuleChange: {
    type: Boolean,
    default: true
  },
  // 是否显示按钮
  showButtons: {
    type: Boolean,
    default: true
  },
  // 是否显示取消按钮
  showCancelButton: {
    type: Boolean,
    default: true
  },
  // 提交按钮文本
  submitText: {
    type: String,
    default: '提交'
  },
  // 取消按钮文本
  cancelText: {
    type: String,
    default: '取消'
  },
  // 加载状态
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'cancel',
  'field-change',
  'validate'
]);

const formRef = ref(null);
const formData = reactive(cloneDeep(props.modelValue));

// 计算表单规则
const formRules = computed(() => {
  const rules = { ...props.rules };
  
  // 合并字段中的规则
  props.fields.forEach(field => {
    if (field.rules) {
      rules[field.prop] = field.rules;
    } else if (field.required && !rules[field.prop]) {
      rules[field.prop] = [{ required: true, message: `请输入${field.label}`, trigger: 'blur' }];
    }
  });
  
  return rules;
});

// 监听modelValue变化
watch(() => props.modelValue, (newVal) => {
  Object.keys(formData).forEach(key => {
    delete formData[key];
  });
  Object.assign(formData, cloneDeep(newVal));
}, { deep: true });

// 监听formData变化
watch(formData, (newVal) => {
  emit('update:modelValue', cloneDeep(newVal));
}, { deep: true });

// 判断字段是否可见
const isFieldVisible = (field) => {
  if (field.visible === false) return false;
  if (typeof field.visible === 'function') {
    return field.visible(formData);
  }
  return true;
};

// 处理字段值变化
const handleFieldChange = (prop, value, isChange = false) => {
  if (isChange) {
    emit('field-change', { prop, value });
    
    // 处理联动
    props.fields.forEach(field => {
      if (field.depends && field.depends.includes(prop)) {
        if (typeof field.update === 'function') {
          const newValue = field.update(formData);
          if (newValue !== undefined) {
            formData[field.prop] = newValue;
          }
        }
      }
    });
  }
};

// 处理上传成功
const handleUploadSuccess = (prop, response, file, fileList) => {
  formData[prop] = fileList;
  emit('field-change', { prop, value: fileList });
};

// 处理上传移除
const handleUploadRemove = (prop, file, fileList) => {
  formData[prop] = fileList;
  emit('field-change', { prop, value: fileList });
};

// 处理表单提交
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    emit('submit', cloneDeep(formData));
  } catch (error) {
    emit('validate', error);
    console.error('表单验证失败:', error);
  }
};

// 处理取消
const handleCancel = () => {
  emit('cancel');
};

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

// 验证表单
const validateForm = async () => {
  if (!formRef.value) return false;
  
  try {
    await formRef.value.validate();
    return true;
  } catch (error) {
    return false;
  }
};

// 暴露方法
defineExpose({
  formRef,
  resetForm,
  validateForm
});

// 初始化
onMounted(() => {
  // 初始化依赖字段的值
  props.fields.forEach(field => {
    if (field.depends && typeof field.update === 'function') {
      const newValue = field.update(formData);
      if (newValue !== undefined) {
        formData[field.prop] = newValue;
      }
    }
  });
});
</script>

<style scoped>
.form-generator {
  width: 100%;
}

.form-divider {
  margin: 20px 0;
}

.divider-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #303133;
}

.form-item-description {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.form-buttons {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style> 